/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo, useState } from "react";
import { useWallet } from "../../hooks/useWallet";
import { Token, Invoice } from "../../types";
import { Shield, Loader2, CheckCircle } from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { InvoicePreview } from "./invoice/InvoicePreview";

export const InvoiceView: React.FC = () => {
  const { walletAddress, balances, generateStealthAddress } = useWallet();

  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [search, setSearch] = useState("");
  const [expires, setExpires] = useState(30);
  const [stealth, setStealth] = useState(true);

  const [token, setToken] = useState<Token | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);

  const filteredTokens = useMemo(() => {
    if (!search) return balances;
    return balances.filter(t =>
      t.symbol.toLowerCase().includes(search.toLowerCase()) || 
      t.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, balances]);

  async function createInvoice() {
    if (!token || !amount || !walletAddress) return;
    setLoading(true);

    try {
      let payAddress = walletAddress;

      if (stealth) {
        payAddress = await generateStealthAddress(token.chain);
      }
      
      // Mocking API call
      await new Promise(res => setTimeout(res, 1000));
      const newInvoice: Invoice = {
        id: `inv_${Date.now()}`,
        merchant: walletAddress,
        token: token.symbol,
        chain: token.chain,
        amount: parseFloat(amount),
        description: desc,
        expiresAt: Date.now() + expires * 60 * 1000,
        address: payAddress,
        stealth,
        status: "unpaid",
        createdAt: Date.now(),
      };

      setInvoice(newInvoice);
    } finally {
      setLoading(false);
    }
  }

  function getDeepLink() {
    if (!invoice) return '';

    if (invoice.chain === "SOL") {
      const params = new URLSearchParams();
      params.set('recipient', invoice.address);
      params.set('amount', invoice.amount.toString());
      // For any token that is not native SOL, treat it as an SPL token and add the mint address.
      if (invoice.token !== 'SOL') {
        const mint = balances.find(t => t.symbol === invoice.token && t.chain === 'SOL')?.mint;
        if (mint) {
          params.set('spl-token', mint);
        }
      }
      params.set('reference', invoice.id);
      params.set('label', "GhostPay Invoice");
      if(invoice.description) params.set('message', `Payment for: ${invoice.description}`);
      
      return `solana:${invoice.address}?${params.toString()}`;
    }

    // Sui deeplink is less standardized, using a common convention
    return `sui:pay?recipient=${invoice.address}&amount=${invoice.amount}&coinType=${balances.find(t => t.symbol === invoice.token)?.mint || '0x2::sui::SUI'}&ref=${invoice.id}`;
  }
  
  if (invoice) {
    return <InvoicePreview invoice={invoice} link={getDeepLink()} onReset={() => setInvoice(null)} />;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="font-serif text-3xl text-[#0A0B0F] dark:text-white">
        Create Invoice
      </h1>

      <Card>
        <div className="space-y-6">
          {/* Token */}
          <div>
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Token</label>
            <input
              className="w-full mt-1 p-3 bg-gray-50 dark:bg-white/5 text-[#0A0B0F] dark:text-white rounded-lg border border-gray-200 dark:border-white/10 focus:border-nobel-gold outline-none"
              placeholder="Search token..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 custom-scrollbar">
              {filteredTokens.map(t => (
                <button
                  key={t.mint}
                  onClick={() => { setToken(t); setSearch('') }}
                  className={`flex w-full items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-white/10 ${
                    token?.mint === t.mint ? "bg-gray-100 dark:bg-white/10" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <img src={t.icon} className="h-6 w-6 rounded-full" />
                    <span className="font-bold text-[#0A0B0F] dark:text-white">{t.symbol}</span>
                    <span className="text-xs text-gray-400">
                      {t.name}
                    </span>
                  </div>
                  {token?.mint === t.mint && <CheckCircle size={16} className="text-green-500" />}
                </button>
              ))}
            </div>
            {token && <div className="mt-2 text-xs text-gray-500">Selected: <span className="font-bold text-[#0A0B0F] dark:text-white">{token.symbol}</span></div>}
          </div>

          {/* Amount */}
          <div>
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</label>
            <input
              type="number"
              className="w-full mt-1 p-3 bg-gray-50 dark:bg-white/5 text-[#0A0B0F] dark:text-white rounded-lg border border-gray-200 dark:border-white/10 focus:border-nobel-gold outline-none"
              placeholder="0.00"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</label>
            <input
              className="w-full mt-1 p-3 bg-gray-50 dark:bg-white/5 text-[#0A0B0F] dark:text-white rounded-lg border border-gray-200 dark:border-white/10 focus:border-nobel-gold outline-none"
              placeholder="Optional memo (e.g., 'Design Services')"
              value={desc}
              onChange={e => setDesc(e.target.value)}
            />
          </div>

          {/* Expiry */}
          <div>
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Expires in (minutes)
            </label>
            <input
              type="number"
              className="w-full mt-1 p-3 bg-gray-50 dark:bg-white/5 text-[#0A0B0F] dark:text-white rounded-lg border border-gray-200 dark:border-white/10 focus:border-nobel-gold outline-none"
              value={expires}
              onChange={e => setExpires(parseInt(e.target.value))}
            />
          </div>

          {/* Stealth */}
          <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              id="stealth-check"
              checked={stealth}
              onChange={e => setStealth(e.target.checked)}
              className="h-4 w-4 rounded accent-nobel-gold"
            />
            <label htmlFor="stealth-check" className="text-sm font-medium flex items-center gap-2">
                Stealth Escrow Address
                <Shield className="h-4 w-4 text-nobel-gold" />
            </label>
          </div>
        </div>
      </Card>

      {/* Action */}
      <Button
        disabled={loading || !token || !amount}
        onClick={createInvoice}
        variant="primary"
        size="lg"
        className="w-full"
      >
        {loading ? <><Loader2 className="animate-spin mr-2" /> Creating...</> : "Generate Invoice"}
      </Button>
    </div>
  );
};